import React, { FC } from 'react';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { BlueButton, Box, Button, Divider } from '~Styles';
import { ModalContent } from '~Styles/modal';
import {
  formatPosition,
  getCorrectValueBasedOnCompType,
  getStaffName,
} from '~Providers/CompetitionsProvider/utils';
import { useButton, useCurrency, useDispatch, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import Heading from '~ComponentsImproved/Typography/Heading';
import { GetCompetitions_Venue_competitions_score } from '~Providers/CompetitionsProvider/__queries__';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';
import useI18n from '~Translations';
import { handyBreakpoint } from '~Styles/breakpoints';
import { PrintRow } from '~Components/PrinterManager';

const Content = styled.div`
  text-align: center;
`;

const PrintButtonWrapper = styled(Button)`
  min-height: 32px;
  padding: 12px;
  white-space: nowrap;

  ${handyBreakpoint} {
    min-height: 24px;
    padding: 8px;
  }
`;

interface PrintButtonProps {
  onClick: () => void;
  name: string;
  size?: 'small' | 'medium' | 'large';
}

const PrintButton: FC<PrintButtonProps> = ({ onClick, name, size }) => {
  const button = useButton({
    onClick: onClick,
  });

  return (
    <Button size={size} {...button}>
      {name}
    </Button>
  );
};

const DEFAULT_FONT_SIZE = 14;

const pad = (spacing = 0) => ' '.repeat(spacing);
const lineBreak = { content: ' ', fontSize: DEFAULT_FONT_SIZE };
const divider = [
  { content: ' ', fontSize: DEFAULT_FONT_SIZE },
  { content: '───────────────────────────────────', fontSize: DEFAULT_FONT_SIZE },
  { content: ' ', fontSize: DEFAULT_FONT_SIZE },
];

const formatPrintContent = ({
  content,
  spacing = 1,
  fontSize = DEFAULT_FONT_SIZE,
  breakLine,
  alignment = 'LEFT',
}: {
  content: string;
  spacing?: number;
  fontSize?: number;
  breakLine?: boolean;
  alignment?: string;
}) => ({
  content: `${pad(spacing)}${content}${breakLine ? '\n' : ''}`,
  fontSize,
  params: {
    align: alignment,
  },
});

interface Props {
  isHandy?: boolean;
  competitionId: string;
}

const PrintScore: FC<Props> = ({ isHandy, competitionId }) => {
  const { i18n } = useI18n();
  const modal = useModal();
  const competitions = useCompetition();
  const dispatch = useDispatch();
  const currency = useCurrency();
  const logger = useLogger('competitions');

  const allCompetitions = [...competitions.onGoing, ...competitions.finished];
  const selectedCompetition = allCompetitions.find((comp) => comp.id === competitionId);
  const participants = selectedCompetition?.score;

  const staff = useSelector((state) => state.companyStaff)?.staffs ?? [];
  const printers = useSelector((state) => state.printers);

  const printScoreButton = useButton({
    onClick: () => modal.show(),
  });

  const printAllButton = useButton({
    onClick: () => print('all'),
  });

  const printSummaryButton = useButton({
    onClick: () => print('summary'),
  });

  if (!selectedCompetition) {
    return null;
  }

  const compHasSpecificSales =
    selectedCompetition.onlySpecificSales?.items?.length > 0 ||
    selectedCompetition.onlySpecificSales?.categories?.length > 0;

  const getParticipantContent = (
    participant: GetCompetitions_Venue_competitions_score,
  ): PrintRow[] => {
    if (!participants) return [];

    const place = participants.indexOf(participant) + 1;

    const value = getCorrectValueBasedOnCompType(
      participant,
      selectedCompetition.type,
      compHasSpecificSales,
      currency.format,
      i18n,
    );

    const printContent = [
      formatPrintContent({
        content: selectedCompetition?.name ?? '',
        fontSize: 18,
        breakLine: true,
      }),
      formatPrintContent({
        content: DateTime.now().toFormat('yyyy-MM-dd HH:mm'),
        fontSize: 14,
        breakLine: true,
      }),
      formatPrintContent({
        content: getStaffName(participant.staffId, staff, i18n),
        fontSize: 14,
      }),
      ...divider,
      formatPrintContent({ content: formatPosition(place, i18n), fontSize: 18 }),
      formatPrintContent({ content: value, fontSize: 17 }),
      lineBreak,
      formatPrintContent({
        content: i18n('Competitions.EndDate') + ':',
        fontSize: 12,
      }),
      formatPrintContent({
        content: DateTime.fromISO(selectedCompetition.endDate).toFormat('yyyy-MM-dd HH:mm'),
        fontSize: 12,
      }),
    ];

    return printContent;
  };

  const print = (type: string) => {
    const foundPrimaryPrinter = printers.find((printer) => printer.primary);

    if (!foundPrimaryPrinter) {
      logger.info('No primary printer found');
      dispatch(actions.addToast(i18n('Competitions.NoPrinter'), true, null, true));
      return;
    }

    if (!participants) {
      return;
    }

    if (type === 'all') {
      participants?.forEach((participant) => {
        const content = getParticipantContent(participant);

        // PRINT HERE
        window.printerManager?.initiatePrint({
          printerId: foundPrimaryPrinter.id,
          data: content,
          printType: 'COMPETITION_SCORE',
        });
      });
    } else if (type === 'summary') {
      const content = [
        formatPrintContent({
          content: selectedCompetition?.name ?? '',
          fontSize: 18,
          breakLine: true,
        }),
        formatPrintContent({
          content: DateTime.now().toFormat('yyyy-MM-dd HH:mm'),
          fontSize: 14,
          breakLine: true,
        }),
        ...divider,
        ...participants?.map((participant) => {
          const value = getCorrectValueBasedOnCompType(
            participant,
            selectedCompetition.type,
            compHasSpecificSales,
            currency.format,
            i18n,
          );

          return formatPrintContent({
            content: `${getStaffName(participant.staffId, staff, i18n)}: ${value}`,
            fontSize: 14,
          });
        }),
        lineBreak,
        formatPrintContent({
          content:
            i18n('Competitions.EndDate') +
            ': ' +
            DateTime.fromISO(selectedCompetition.endDate).toFormat('yyyy-MM-dd HH:mm'),
          fontSize: 12,
          breakLine: true,
        }),
      ];

      window.printerManager?.initiatePrint({
        printerId: foundPrimaryPrinter.id,
        data: content,
        printType: 'COMPETITION_SCORE',
      });
    } else {
      // This is a specific participant
      const foundParticipant = participants?.find((participant) => participant.staffId === type);
      if (!foundParticipant) return;
      const content = getParticipantContent(foundParticipant);

      window.printerManager?.initiatePrint({
        printerId: foundPrimaryPrinter.id,
        data: content,
        printType: 'COMPETITION_SCORE',
      });
    }
  };

  const sortedAlpabetically = participants
    ? [...participants]?.sort((a, b) => {
        const aName = getStaffName(a.staffId, staff, i18n);
        const bName = getStaffName(b.staffId, staff, i18n);
        return aName.localeCompare(bName);
      })
    : [];

  return (
    <div>
      <PrintButtonWrapper {...printScoreButton}>
        {i18n('Competitions.Print.Button')}
      </PrintButtonWrapper>

      <Modal isShowing={modal.isShowing} hide={() => modal.hide()}>
        <ModalContent>
          <Content>
            <Heading level={isHandy ? 3 : 1} color="var(--colorWhite)" bottom={32}>
              {i18n('Competitions.Print.Title')}
            </Heading>

            <Box display="flex" gap={isHandy ? 'small' : 'large'} justifyContent="center">
              <BlueButton size={isHandy ? 'small' : 'medium'} {...printAllButton}>
                {i18n('Competitions.PrintAll')}
              </BlueButton>
              <Button size={isHandy ? 'small' : 'medium'} {...printSummaryButton}>
                {i18n('Competitions.PrintSummary')}
              </Button>
            </Box>

            <Box marginTop="large" marginBottom="medium">
              <Divider color="var(--colorBlueGray800)" />
            </Box>

            <div>
              <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)" bottom={16}>
                {i18n('Competitions.PrintSpecific.Text')}
              </Paragraph>

              <Box display="flex" gap="small" flexWrap="wrap" justifyContent="center">
                {sortedAlpabetically?.map((score) => (
                  <div key={score.staffId}>
                    <PrintButton
                      size={isHandy ? 'small' : 'medium'}
                      name={getStaffName(score.staffId, staff, i18n)}
                      onClick={() => print(score.staffId)}
                    />
                  </div>
                ))}
              </Box>
            </div>
          </Content>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default PrintScore;
